<!--主布局页面-->
<template>
  <el-container class="layout-container">
    <el-header class="layout-header">
      <Header
        @toggle-menu="handleToggleSidebar"
        @toggle-mobile-menu="toggleMobileMenu"
      />
    </el-header>
    <el-container class="layout-content">
      <!-- 手机端遮罩层 -->
      <div
        v-if="isMobile"
        class="mobile-mask"
        :class="{ show: mobileMenuOpen }"
        @click="closeMobileMenu"
      ></div>
      <el-aside
        class="layout-sidebar"
        :class="{ 'is-collapse': isCollapse }"
        :style="sidebarStyle"
      >
        <Sidebar @close-mobile-menu="closeMobileMenu" />
      </el-aside>
      <el-main
        class="layout-main"
        :style="mainStyle"
        @click="closeMobileMenuOnMainClick"
      >
        <Tabs />
        <el-scrollbar
          ref="scrollbarRef"
          wrap-class="scrollbar-wrapper"
          view-class="scrollbar-view"
          class="main-scrollbar"
        >
          <div class="layout-view-content">
            <router-view v-slot="{ Component }">
              <keep-alive :include="cachedViews">
                <component
                  :is="Component"
                  class="page-content"
                />
              </keep-alive>
            </router-view>
          </div>
        </el-scrollbar>
      </el-main>
    </el-container>
    <!-- 手机端折叠/展开箭头按钮 - 紧贴侧边栏右边框 -->
    <div
      v-if="isMobile && mobileMenuOpen"
      class="fold-arrow-btn"
      :class="{ 'is-collapsed': isCollapse }"
      @click="handleMobileFoldToggle"
    >
      <div class="arrow-icon">
        <el-icon>
          <DArrowLeft v-if="!isCollapse" />
          <DArrowRight v-else />
        </el-icon>
      </div>
    </div>
  </el-container>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Fold, Expand } from '@element-plus/icons-vue'
import { Header, Sidebar, Tabs } from "./components"
import { sidebarConfig } from '../config/sidebar.config'
import { useMenuStore, useTabsStore } from '@/stores'

const menuStore = useMenuStore()
const tabsStore = useTabsStore()

//===========手机端响应式状态===========
const isMobile = ref(false)
const mobileMenuOpen = ref(false)

// ========== 检测是否为手机端 ==========
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
  // 手机端时强制关闭菜单
  if (isMobile.value) {
    mobileMenuOpen.value = false
  }
}

// ========== PC端切换侧边栏 ==========
const handleToggleSidebar = () => {
  if (!isMobile.value) {
    menuStore.toggleCollapse()
  }
}

// ========== 手机端折叠/展开侧边栏（悬浮按钮使用） ==========
const handleMobileFoldToggle = () => {
  if (isMobile.value && mobileMenuOpen.value) {
    menuStore.collapsed = !menuStore.collapsed
  }
}

// 切换手机端菜单
const toggleMobileMenu = () => {
  if (isMobile.value) {
    mobileMenuOpen.value = !mobileMenuOpen.value
    // 控制body滚动，防止滚动穿透
    if (mobileMenuOpen.value) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    // 同步状态到全局
    window.dispatchEvent(new CustomEvent('mobile-menu-state', {
      detail: { open: mobileMenuOpen.value }
    }))
  }
}

// 关闭手机端菜单
const closeMobileMenu = () => {
  if (mobileMenuOpen.value) {
    mobileMenuOpen.value = false
    document.body.style.overflow = ''
    window.dispatchEvent(new CustomEvent('mobile-menu-state', {
      detail: { open: false }
    }))
  }
}

// 点击主内容区域关闭菜单（手机端）
const closeMobileMenuOnMainClick = () => {
  if (isMobile.value && mobileMenuOpen.value) {
    closeMobileMenu()
  }
}

// 监听窗口大小变化
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  document.body.style.overflow = ''
})

//===========计算属性===========
const isCollapse = computed(() => menuStore.isCollapsed)

// 侧边栏宽度计算
const sidebarWidth = computed(() =>
  isCollapse.value ? sidebarConfig.collapsedWidth : sidebarConfig.width
)

const cachedViews = computed(() => tabsStore.cachedViews || [])

// 侧边栏样式 - 手机端菜单栏本身透明度
const sidebarStyle = computed(() => {
  // PC端返回正常样式（无透明度）
  if (!isMobile.value) {
    return {
      width: `${sidebarWidth.value}px`,
      '--sidebar-bg-color': sidebarConfig.backgroundColor || '#304156',
      '--sidebar-text-color': sidebarConfig.textColor || '#bfcbd9'
    }
  }
  // 手机端：菜单栏本身有透明度效果
  return {
    width: `${sidebarWidth.value}px`,
    // 控制位置动画
    transform: mobileMenuOpen.value ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease-in-out',
  }
})

// 主内容区域样式
const mainStyle = computed(() => {
  if (isMobile.value) {
    return {
      marginLeft: '0',
      width: '100%'
    }
  }
  return {
    marginLeft: `${sidebarWidth.value}px`,
    width: `calc(100% - ${sidebarWidth.value}px)`,
    transition: 'margin-left 0.3s ease, width 0.3s ease'
  }
})
</script>

<style lang="scss" scoped>
.layout-container {
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: #fefefe;
}
.layout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100% !important;
  height: 54px;
  padding: 0;
  background-color: #008dbc;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  flex-shrink: 0;
  z-index: 1002;
}
.layout-content {
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
}
.layout-sidebar {
  position: fixed;
  left: 0;
  top: 54px;
  bottom: 0;
  flex-shrink: 0;
  display: flex;
  background-color: var(--sidebar-bg-color);
  color: var(--sidebar-text-color);
  border-right: #dcdfe6 solid 1px;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1001;
  // PC端样式
  transition: width 0.3s ease;
  &.is-collapse {
    :deep(.el-menu) {
      .el-sub-menu__title,
      .el-menu-item {
        justify-content: center;
        .menu-icon {
          margin-right: 0;
        }
        .menu-title,
        .el-sub-menu__icon-arrow,
        .menu-badge,
        .external-icon {
          display: none;
        }
      }
    }
  }
}
.layout-main {
  --el-main-padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  transition: margin-left 0.3s ease, width 0.3s ease;
}
.main-scrollbar {
  flex: 1;
  min-height: 0;
  height: 0;
  :deep(.el-scrollbar__wrap) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch !important;
    scroll-behavior: smooth !important;
    overscroll-behavior: contain !important;
  }
  :deep(.el-scrollbar__view) {
    height: 100%;
  }
}
.layout-view-content {
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: 2px;
  display: flex;
  flex-direction: column;
  .page-content {
    background-color: #fff;
    padding: 5px;
    width: 100%;
    flex: 1;
    min-height: 0;
    border-radius: 5px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
    animation: slideInLeft 1s ease;
    display: flex;
    flex-direction: column;
    &[data-updating] {
      animation: pulseBorder 1s infinite;
    }
  }
}

//页面入场动画，左侧滑入
@keyframes slideInLeft {
  0% {
    opacity: 0;
    transform: translateX(-60px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulseBorder {
  0% { box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(64, 158, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(64, 158, 255, 0); }
}

.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all 0.3s ease;
}

.fade-transform-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 响应式样式 */
@media (max-width: 768px) {
  .layout-header {
    height: 50px;
  }
  .layout-sidebar {
    top: 50px;
    // 移除CSS中的transition，由JS控制
    transition: none !important;
    // 让侧边栏容器背景透明，以便子组件的透明度显示
    background-color: transparent !important;
    // 确保侧边栏有较高的层级
    z-index: 1001;
  }
  .layout-main {
    margin-left: 0 !important;
    width: 100% !important;
  }
  .layout-view-content {
    padding: 12px;
    .page-content {
      padding: 16px;
      border-radius: 6px;
    }
  }
  // 折叠/展开箭头按钮 - 紧贴侧边栏右边框
  .fold-arrow-btn {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #008dbc;
    border-radius: 0 8px 8px 0;
    cursor: pointer;
    z-index: 1002;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    left: 200px; // 默认宽度200px时紧贴右边框
    // 侧边栏折叠时的位置
    &.is-collapsed {
      left: 64px; // 折叠后宽度64px时紧贴右边框
    }
    .arrow-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      .el-icon {
        font-size: 16px;
        color: #fff;
        transition: transform 0.2s ease;
      }
    }
    &:hover {
      background: rgba(0, 141, 188, 0.8);
    }
    &:active {
      transform: translateY(-50%) scale(0.96);
    }
  }
  // 水波纹效果（可选）
  @keyframes rippleArrow {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 141, 188, 0.4);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(0, 141, 188, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 141, 188, 0);
    }
  }
}

// 手机端遮罩层样式
.mobile-mask {
  position: fixed;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  &.show {
    opacity: 1;
    visibility: visible;
  }
}

@media (max-width: 480px) {
  .layout-view-content {
    padding: 8px;
    .page-content {
      padding: 12px;
    }
  }
}
</style>

